import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin/chatbot")({
  
  head: () => ({ meta: [{ title: "Admin — Chatbot | Mine Class" }, { name: "description", content: "Panel de administración de Chatbot — acceso restringido a Mine Class." }, { name: "robots", content: "noindex, nofollow" }] }),
  component: ChatbotAdmin,
});

type Lead = {
  id: string;
  created_at: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  company: string | null;
  interest: string | null;
  message: string | null;
  status: string | null;
};

type Filter = "todos" | "persona" | "empresa";

function ChatbotAdmin() {
  const [rows, setRows] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("todos");
  const [q, setQ] = useState("");

  async function load() {
    setLoading(true);
    setErr(null);
    const { data, error } = await supabase
      .from("leads")
      .select("id, created_at, first_name, last_name, email, company, interest, message, status")
      .like("interest", "Chatbot%")
      .order("created_at", { ascending: false });
    if (error) setErr(error.message);
    else setRows((data ?? []) as Lead[]);
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (filter === "persona" && r.interest !== "Chatbot Persona") return false;
      if (filter === "empresa" && r.interest !== "Chatbot Empresa") return false;
      if (q.trim()) {
        const hay = `${r.first_name ?? ""} ${r.last_name ?? ""} ${r.email} ${r.company ?? ""} ${r.message ?? ""}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [rows, filter, q]);

  function exportCSV() {
    const headers = ["Fecha", "Tipo", "Nombre", "Email", "Empresa / RUT", "Consulta", "Estado"];
    const lines = filtered.map((r) => {
      const tipo = r.interest === "Chatbot Empresa" ? "Empresa" : "Persona";
      const nombre = `${r.first_name ?? ""} ${r.last_name ?? ""}`.trim();
      const extra = tipo === "Empresa" ? (r.company ?? "") : rutFromMessage(r.message);
      const consulta = tipo === "Persona" ? consultaFromMessage(r.message) : (r.message ?? "");
      return [new Date(r.created_at).toISOString(), tipo, nombre, r.email, extra, consulta, r.status ?? ""]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(",");
    });
    const csv = [headers.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `chatbot-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function updateStatus(id: string, status: string) {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    const { error } = await supabase.from("leads").update({ status: status as "new" | "contacted" | "qualified" | "won" | "lost" }).eq("id", id);
    if (error) {
      setErr(error.message);
      load();
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[var(--color-brand-purple)]">Chatbot · Mensajes recibidos</h2>
          <p className="text-sm text-[var(--color-on-surface-variant)]">Consultas enviadas desde el chatbot flotante del sitio.</p>
        </div>
        <button onClick={exportCSV} className="px-4 py-2 bg-[var(--color-brand-coral)] text-white text-sm font-bold uppercase tracking-widest">
          Exportar CSV
        </button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {(["todos", "persona", "empresa"] as Filter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-2 text-sm font-semibold border ${
              filter === f
                ? "bg-[var(--color-brand-purple)] text-white border-[var(--color-brand-purple)]"
                : "bg-white text-[var(--color-brand-purple)] border-[var(--color-outline-variant)]"
            }`}
          >
            {f === "todos" ? "Todos" : f === "persona" ? "Personas" : "Empresas"}
          </button>
        ))}
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar nombre, email, empresa..."
          className="ml-auto border border-[var(--color-outline-variant)] px-3 py-2 text-sm w-full md:w-80 outline-none focus:border-[var(--color-brand-coral)]"
        />
      </div>

      {err && <p className="text-sm text-red-600">{err}</p>}
      {loading ? (
        <p className="text-sm text-[var(--color-on-surface-variant)]">Cargando...</p>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-[var(--color-outline-variant)] p-8 text-center text-sm text-[var(--color-on-surface-variant)]">
          Aún no hay mensajes del chatbot.
        </div>
      ) : (
        <div className="bg-white border border-[var(--color-outline-variant)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-surface-container-low)] text-left text-xs uppercase tracking-wider text-[var(--color-on-surface-variant)]">
              <tr>
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2">Tipo</th>
                <th className="px-3 py-2">Nombre</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Empresa / RUT</th>
                <th className="px-3 py-2">Consulta</th>
                <th className="px-3 py-2">Estado</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const tipo = r.interest === "Chatbot Empresa" ? "Empresa" : "Persona";
                const nombre = `${r.first_name ?? ""} ${r.last_name ?? ""}`.trim() || "—";
                const extra = tipo === "Empresa" ? r.company ?? "—" : rutFromMessage(r.message) || "—";
                const consulta = tipo === "Persona" ? consultaFromMessage(r.message) : r.message ?? "";
                return (
                  <tr key={r.id} className="border-t border-[var(--color-outline-variant)] align-top">
                    <td className="px-3 py-3 whitespace-nowrap">{new Date(r.created_at).toLocaleString("es-CL")}</td>
                    <td className="px-3 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 text-xs font-bold ${
                          tipo === "Empresa" ? "bg-[var(--color-brand-purple)] text-white" : "bg-[var(--color-brand-coral)] text-white"
                        }`}
                      >
                        {tipo}
                      </span>
                    </td>
                    <td className="px-3 py-3">{nombre}</td>
                    <td className="px-3 py-3">
                      <a href={`mailto:${r.email}`} className="text-[var(--color-brand-purple)] underline">
                        {r.email}
                      </a>
                    </td>
                    <td className="px-3 py-3">{extra}</td>
                    <td className="px-3 py-3 max-w-md whitespace-pre-wrap">{consulta || "—"}</td>
                    <td className="px-3 py-3">
                      <select
                        value={r.status ?? "new"}
                        onChange={(e) => updateStatus(r.id, e.target.value)}
                        className="border border-[var(--color-outline-variant)] px-2 py-1 text-xs"
                      >
                        <option value="new">Nuevo</option>
                        <option value="contacted">Contactado</option>
                        <option value="qualified">Calificado</option>
                        <option value="won">Ganado</option>
                        <option value="lost">Descartado</option>
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function rutFromMessage(m: string | null): string {
  if (!m) return "";
  const match = m.match(/RUT:\s*([^\n]+)/i);
  return match ? match[1].trim() : "";
}

function consultaFromMessage(m: string | null): string {
  if (!m) return "";
  return m.replace(/^RUT:\s*[^\n]+\n*/i, "").trim();
}
